import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useCallback } from 'react';
import { styled } from '@mui/material';
import MuiTypography from '@mui/material/Typography';
import MuiBox from '@mui/material/Box';
import Hint from '../Hint/Hint.js';
import Counter from '../Counter/Counter.js';

const SelectableButtons = styled(MuiBox, {
    shouldForwardProp: prop => !['fixed', 'options'].includes(prop),
}) `
  display: flex;
  ${({ fixed, options }) => fixed &&
    `
      display: grid;
      grid-template-columns: repeat(${options.length}, 1fr);`}
`;
const OptionContainer = styled(MuiBox, {
    shouldForwardProp: prop => !['selected', 'disabled', 'hideCounter'].includes(prop),
}) `
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme, hideCounter, disabled, selected }) => `
    transition: background-color ${theme.transitions.duration.fast} linear, color ${theme.transitions.duration.fast} linear;
    position: relative;
    outline: none;
    padding: ${theme.spacing(1.75)} ${theme.spacing(3)} ${theme.spacing(2)};
    background-color: ${theme.palette[selected ? 'N40' : 'N70'].main};
    color: ${theme.palette[disabled ? (selected ? 'N30' : 'N40') : selected ? 'N10' : 'N30'].main};
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    border: 1px solid ${theme.palette.N70.main};
    &:first-of-type {
      border-left: 1px solid ${theme.palette.N70.main};
      border-top-left-radius: ${theme.spacing(1)};
      border-bottom-left-radius: ${theme.spacing(1)};
    }
    &:last-of-type {
      border-right: 1px solid ${theme.palette.N70.main};
      border-top-right-radius: ${theme.spacing(1)};
      border-bottom-right-radius: ${theme.spacing(1)};
    }
    :before {
      border-radius: 7px;
      background-color: transparent;
      transition: border ${theme.transitions.duration.fast} linear;
      border: 1px solid ${theme.palette.primary.light};
      content: '';
      display: none;
      position: absolute;
      z-index: 1;
      top: -3px;
      left: -3px;
      right: -3px;
      bottom: -3px;
    }
    &:focus-visible:before {
      display: block;
    }
    ${!disabled &&
    `&:hover {
      color: ${theme.palette[selected ? 'N05' : 'N20'].main};
      ${!selected && `background-color: ${theme.palette.N50.main};`}
     }`};
    ${!hideCounter &&
    `> *:last-child {
        color: ${theme.palette[selected ? (disabled ? 'N30' : 'N10') : 'N70'].main};
        background-color: ${theme.palette[disabled ? 'N40' : 'N30'].main};
      }
      ${!disabled &&
        ` &:hover > *:last-child {
        color: ${theme.palette[selected ? 'N05' : 'N70'].main};
        ${!selected && `background-color: ${theme.palette.N20.main}`};
      }`}`}
  `}
`;
const _Counter = styled(Counter) `
  pointer-events: none;
  ${({ theme }) => `
  margin: ${theme.spacing(0, 0, 0, 2)};
  transition: background-color ${theme.transitions.duration.fast} linear, color ${theme.transitions.duration.fast} linear;
  `}
`;
const SwitchOption = ({ onToggle, hideCounter, disabled, option }) => {
    const { selected, count = 0, hint, hintDuration, 'data-testid': testId } = option;
    const label = option.label;
    const icon = option.icon;
    const onToggleInternal = useCallback(() => {
        if (!disabled && !option.disabled) {
            onToggle(Object.assign(Object.assign({}, option), { selected: !selected }));
        }
    }, [disabled, onToggle, option, selected]);
    return (jsx(Hint, Object.assign({ title: hint, duration: hintDuration }, { children: jsxs(OptionContainer, Object.assign({ tabIndex: disabled || option.disabled ? undefined : 0, selected: selected, disabled: disabled || option.disabled, hideCounter: hideCounter, onClick: onToggleInternal, "data-testid": testId }, { children: [icon, label && (jsx(MuiTypography, Object.assign({ variant: "subtitle2", noWrap: true }, { children: label }))), !hideCounter && jsx(_Counter, { count: count })] })) })));
};
const ContentSwitch = (_a) => {
    var { onSelectionChange, options, hideCounter, multiSelect, disabled } = _a, rest = __rest(_a, ["onSelectionChange", "options", "hideCounter", "multiSelect", "disabled"]);
    const onToggle = useCallback((option) => {
        onSelectionChange(options.map(item => {
            if (item.id === option.id) {
                return option;
            }
            return multiSelect ? item : Object.assign(Object.assign({}, item), { selected: false });
        }));
    }, [options, onSelectionChange, multiSelect]);
    return (jsx(SelectableButtons, Object.assign({ options: options }, rest, { children: options.map(option => (jsx(SwitchOption, { onToggle: onToggle, hideCounter: hideCounter, option: option, disabled: disabled }, option.id))) })));
};

export { ContentSwitch as default };
//# sourceMappingURL=ContentSwitch.js.map
